import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "@/components/ui/Spinner";
import TableTitleBar from "@/charts/TableTitleBar";

const ThreeColumnTable = ({
  data,
  column1Key,
  column2Key,
  column3Key,
  column1Title,
  column2Title,
  column3Title,
  title,
  columnRatio = "33-33-34",
  columnAlignment = "l-l-l",
  defaultNumberOfRows = 5,
  wrapLength = null,
  isLoaded = false,
  error = null,
  boxHeight = "400px",
  minCellHeight = "2.5rem",
  pagination = false,
  noDataMessage,
  className = "",
}) => {
  const { t } = useTranslation();
  const resolvedNoDataMessage =
    noDataMessage ?? t("common.noDataGeneric", "No data available");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = defaultNumberOfRows;

  // Parse column ratio to get percentage widths
  const [col1Width, col2Width, col3Width] = columnRatio.split("-").map(Number);

  // Parse column alignment
  const [col1Align, col2Align, col3Align] = columnAlignment.split("-");
  const getAlignmentClass = (align) => {
    switch (align) {
      case "l":
        return "text-start";
      case "r":
        return "text-end";
      case "c":
        return "text-center";
      default:
        return "text-start";
    }
  };

  // Calculate pagination values (only used when pagination is true)
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = pagination ? data.slice(startIndex, endIndex) : data;

  // Check if headers should be shown
  const showHeaders = column1Title || column2Title || column3Title;

  // Function to truncate text if wrapLength is specified
  const truncateText = (text, maxLength) => {
    if (!maxLength || !text || typeof text !== "string") return text;
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  // Helper function to render cell content
  const renderCellContent = (content, alignment, shouldTruncate = true) => {
    if (React.isValidElement(content)) {
      return <div className={getAlignmentClass(alignment)}>{content}</div>;
    }

    // Check if content is a string (HTML or plain text)
    if (typeof content === "string") {
      return (
        <div
          className={`${
            shouldTruncate && !wrapLength ? "text-truncate" : ""
          } ${getAlignmentClass(alignment)}`}
          dangerouslySetInnerHTML={{
            __html: wrapLength ? truncateText(content, wrapLength) : content,
          }}
        />
      );
    }

    // Check if content is an array (for domains, keywords, etc.)
    if (Array.isArray(content)) {
      return (
        <div
          className={`${
            shouldTruncate ? "text-truncate" : ""
          } ${getAlignmentClass(alignment)}`}
        >
          {content.join(", ")}
        </div>
      );
    }

    // For any other type, convert to string
    return (
      <div
        className={`${
          shouldTruncate ? "text-truncate" : ""
        } ${getAlignmentClass(alignment)}`}
      >
        {content != null ? String(content) : ""}
      </div>
    );
  };

  // Navigation handlers
  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const exportColumns = [
    { key: column1Key, title: column1Title || column1Key },
    { key: column2Key, title: column2Title || column2Key },
    { key: column3Key, title: column3Title || column3Key },
  ];

  // Handle error state
  if (error) {
    return (
      <div
        className={`bg-white card-radius border d-flex flex-column${className ? ` ${className}` : ""}`}
        style={{ borderColor: "var(--border)", overflow: "hidden" }}
      >
        {title && (
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="text-sm fw-medium text-dark">{title}</div>
          </div>
        )}
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ flex: 1, minHeight: boxHeight }}
        >
          <div className="w-100 text-center align-self-start m-3">
            <div className="alert alert-danger mb-0" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {t(
                "common.loadError",
                "An error occurred while loading the data. Please try again later.",
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle loading state
  if (!isLoaded) {
    return (
      <div
        className={`bg-white card-radius border d-flex flex-column${className ? ` ${className}` : ""}`}
        style={{ borderColor: "var(--border)", overflow: "hidden" }}
      >
        {title && (
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="text-sm fw-medium text-dark">{title}</div>
          </div>
        )}
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ flex: 1, minHeight: boxHeight }}
        >
          <Spinner />
        </div>
      </div>
    );
  }

  // Handle no data state
  if (isLoaded && data.length === 0) {
    return (
      <div
        className={`bg-white card-radius border d-flex flex-column${className ? ` ${className}` : ""}`}
        style={{ borderColor: "var(--border)", overflow: "hidden" }}
      >
        {title && (
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="text-sm fw-medium text-dark">{title}</div>
          </div>
        )}
        <div
          className="d-flex justify-content-center align-items-center text-muted"
          style={{ flex: 1, minHeight: boxHeight }}
        >
          <div>
            <i className="bi bi-table me-2"></i>
            {resolvedNoDataMessage}
          </div>
        </div>
      </div>
    );
  }

  // Handle loaded data state
  return (
    <div
      className={`bg-white card-radius border d-flex flex-column${className ? ` ${className}` : ""}`}
      style={{ borderColor: "var(--border)", overflow: "hidden" }}
    >
      {title && (
        <TableTitleBar title={title} data={data} columns={exportColumns} />
      )}

      {/* Table Header - Only show if at least one title exists */}
      {showHeaders && (
        <div className="px-3 py-2 border-bottom bg-light">
          <div className="row g-0">
            <div style={{ width: `${col1Width}%` }}>
              <div
                className={`text-sm fw-medium text-dark ${getAlignmentClass(
                  col1Align,
                )}`}
              >
                {column1Title || ""}
              </div>
            </div>
            <div style={{ width: `${col2Width}%` }}>
              <div
                className={`text-sm fw-medium text-dark ${getAlignmentClass(
                  col2Align,
                )}`}
              >
                {column2Title || ""}
              </div>
            </div>
            <div style={{ width: `${col3Width}%` }}>
              <div
                className={`text-sm fw-medium text-dark ${getAlignmentClass(
                  col3Align,
                )}`}
              >
                {column3Title || ""}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table Body */}
      <div
        style={{
          height: pagination ? "auto" : boxHeight,
          overflowY: pagination ? "visible" : "auto",
        }}
      >
        {/* Render actual data rows */}
        {currentData.map((item, index) => (
          <div
            key={item.id || `item-${pagination ? startIndex + index : index}`}
            className="px-3 py-2 text-sm hoverable-row border-bottom"
            style={{ minHeight: minCellHeight }}
          >
            <div className="row g-0 align-items-center">
              <div style={{ width: `${col1Width}%` }}>
                {renderCellContent(item[column1Key], col1Align, !wrapLength)}
              </div>
              <div style={{ width: `${col2Width}%` }}>
                {renderCellContent(item[column2Key], col2Align)}
              </div>
              <div style={{ width: `${col3Width}%` }}>
                {renderCellContent(item[column3Key], col3Align)}
              </div>
            </div>
          </div>
        ))}

        {/* Render empty placeholder rows to maintain constant height (only for pagination) */}
        {pagination &&
          Array.from(
            { length: itemsPerPage - currentData.length },
            (_, index) => (
              <div
                key={`empty-${index}`}
                className="px-3 py-2 text-sm"
                style={{ minHeight: minCellHeight }}
              >
                <div className="row g-0">
                  <div style={{ width: `${col1Width}%` }}>
                    <div
                      className={`text-truncate ${getAlignmentClass(col1Align)}`}
                    >
                      &nbsp;
                    </div>
                  </div>
                  <div style={{ width: `${col2Width}%` }}>
                    <div
                      className={`text-truncate ${getAlignmentClass(col2Align)}`}
                    >
                      &nbsp;
                    </div>
                  </div>
                  <div style={{ width: `${col3Width}%` }}>
                    <div
                      className={`text-truncate ${getAlignmentClass(col3Align)}`}
                    >
                      &nbsp;
                    </div>
                  </div>
                </div>
              </div>
            ),
          )}
      </div>

      {/* Spacer to fill remaining card height when flex-fill is applied */}
      <div style={{ flex: 1 }} />

      {/* Table Footer with Pagination (only show when pagination is true) */}
      {pagination && (
        <div className="px-3 py-2 border-top bg-light">
          <div className="d-flex justify-content-center align-items-center">
            <button
              className="btn btn-sm btn-outline-secondary me-3"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              style={{ minWidth: "30px" }}
            >
              &lt;
            </button>

            <span className="text-sm text-muted">
              {t("common.pageOf", {
                current: currentPage,
                total: totalPages,
                defaultValue: `Page ${currentPage} of ${totalPages}`,
              })}
            </span>

            <button
              className="btn btn-sm btn-outline-secondary ms-3"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              style={{ minWidth: "30px" }}
            >
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThreeColumnTable;
